import { inject, Injectable } from '@angular/core';
import { LogLevel } from './log-level';
import { PcpLoggerInterface } from './pcp-logger.interface';
import { writeEventToConsole, writeMetricToConsole, writeToConsole } from './console-writer';
import {
    CONSOLE_LEVEL_KEY,
    TELEMETRY_LEVEL_KEY,
    getLocalStorageLogLevel,
} from './local-storage-log-level';
import {
    DEFAULT_CONSOLE_LEVEL,
    DEFAULT_TELEMETRY_LEVEL,
    LOGGING_CONFIG,
    LoggingConfig,
} from './logging-config';
import { PCP_TELEMETRY_SINK, TelemetrySink } from './telemetry-sink';

/**
 * The severity a named event or metric counts as **for the console floor only**.
 *
 * Events carry no severity of their own — that is what makes their telemetry delivery
 * ungateable. But the console still needs some answer to "is this worth showing", and `INFO`
 * matches both the writer (`console.info`) and the intent: visible at the local default
 * (`VERBOSE`), suppressed at the deployed default (`WARNING`).
 */
const EVENT_CONSOLE_LEVEL = LogLevel.INFO;

/**
 * Immutable logger instance bound to a specific domain.
 * Created by {@link PcpLoggerService.createScoped}.
 */
class ScopedPcpLogger implements PcpLoggerInterface {
    constructor(
        private readonly sink: TelemetrySink | null,
        private readonly config: LoggingConfig | null,
        private readonly domain: string,
    ) {}

    /** Logs a message at VERBOSE level. */
    verbose(message: string, properties?: Record<string, unknown>): void {
        this.dispatch(LogLevel.VERBOSE, message, properties);
    }

    /** Logs a message at DEBUG level. */
    debug(message: string, properties?: Record<string, unknown>): void {
        this.dispatch(LogLevel.DEBUG, message, properties);
    }

    /** Logs a message at INFO level. */
    info(message: string, properties?: Record<string, unknown>): void {
        this.dispatch(LogLevel.INFO, message, properties);
    }

    /** Logs a message at WARNING level. */
    warn(message: string, properties?: Record<string, unknown>): void {
        this.dispatch(LogLevel.WARNING, message, properties);
    }

    /** Logs a message at ERROR level with an optional {@link Error} instance. */
    error(message: string, error?: Error, properties?: Record<string, unknown>): void {
        this.dispatch(LogLevel.ERROR, message, properties, error);
    }

    /** Logs a message at CRITICAL level with an optional {@link Error} instance. */
    critical(message: string, error?: Error, properties?: Record<string, unknown>): void {
        this.dispatch(LogLevel.CRITICAL, message, properties, error);
    }

    /**
     * Sends a named event: **always** to telemetry, to the console only if it clears the console
     * floor.
     *
     * Delivery is unconditional because this is tracking, not logging — whether a business event
     * occurred is a fact about the world, not a diagnostic. If a verbosity setting could suppress
     * it, every `customEvents` aggregate would silently depend on each installation's config.
     *
     * The *console* half is a developer convenience and is gated like any other console output, so
     * a production console is not sprinkled with unsuppressable `[event]` lines. The two halves
     * are deliberately independent — do not collapse them back into one condition.
     */
    trackEvent(name: string, properties?: Record<string, unknown>): void {
        const eventName = this.domain ? `${this.domain}.${name}` : name;
        if (EVENT_CONSOLE_LEVEL >= this.consoleFloor()) {
            writeEventToConsole(eventName, properties);
        }
        this.sink?.event(eventName, this.enrich(properties));
    }

    /** Sends a named metric value. Same split as {@link trackEvent}: always sent, console gated. */
    trackMetric(name: string, value: number, properties?: Record<string, unknown>): void {
        const metricName = this.domain ? `${this.domain}.${name}` : name;
        if (EVENT_CONSOLE_LEVEL >= this.consoleFloor()) {
            writeMetricToConsole(metricName, value, properties);
        }
        this.sink?.metric(metricName, value, this.enrich(properties));
    }

    /** Returns a child logger whose domain is `parentDomain.domain` (dot-chained). */
    createScoped(domain: string): PcpLoggerInterface {
        const child = this.domain ? `${this.domain}.${domain}` : domain;
        return new ScopedPcpLogger(this.sink, this.config, child);
    }

    /**
     * Routes one message to each destination whose floor it clears.
     *
     * The two floors are evaluated independently, so a message can reach one and not the other.
     */
    private dispatch(level: LogLevel, message: string, properties?: Record<string, unknown>, error?: Error): void {
        if (level >= this.consoleFloor()) {
            writeToConsole(level, this.domain, message, properties, error);
        }
        if (this.sink && level >= this.telemetryFloor()) {
            this.toTelemetry(level, message, properties, error);
        }
    }

    /**
     * The severity → telemetry-shape routing table.
     *
     * Every severity is a **message**, so every severity becomes a trace — except errors, which
     * become exceptions so the stack is carried. Named events come only from `trackEvent`.
     *
     * ⚠️ `info` deliberately does **not** become an event. It did until 2026-09-25, and the event
     * name was the interpolated message, so `log.info('Token refresh scheduled in 1740s')` minted a
     * brand-new event name on every call — 9 distinct names for 13 items in one short session.
     * That makes `customEvents` impossible to aggregate and grows name cardinality without bound.
     * A message is not an event; if something should be counted, give it a name via `trackEvent`.
     *
     * An error without an `Error` instance still becomes an exception, so a failure is never
     * demoted to a trace just because the caller had no object to hand.
     */
    private toTelemetry(level: LogLevel, message: string, properties?: Record<string, unknown>, error?: Error): void {
        const prefixed = this.domain ? `[${this.domain}] ${message}` : message;
        const enriched = this.enrich(properties);

        if (level === LogLevel.ERROR || level === LogLevel.CRITICAL) {
            this.sink?.exception(error ?? new Error(prefixed), level, enriched);
            return;
        }
        this.sink?.trace(level, prefixed, enriched);
    }

    /** Adds the domain as a filterable dimension, so it can be queried without parsing names. */
    private enrich(properties?: Record<string, unknown>): Record<string, unknown> {
        return { ...properties, ...(this.domain ? { 'pcp.domain': this.domain } : {}) };
    }

    /** Precedence: localStorage override → app config → VERBOSE. */
    private consoleFloor(): LogLevel {
        return getLocalStorageLogLevel(CONSOLE_LEVEL_KEY)
            ?? this.config?.consoleLevel
            ?? DEFAULT_CONSOLE_LEVEL;
    }

    /** Precedence: localStorage override → app config → INFO. Independent of the console floor. */
    private telemetryFloor(): LogLevel {
        return getLocalStorageLogLevel(TELEMETRY_LEVEL_KEY)
            ?? this.config?.telemetryLevel
            ?? DEFAULT_TELEMETRY_LEVEL;
    }
}

/**
 * The logging service. There is exactly one.
 *
 * Not intended for direct use — inject {@link PCP_LOGGER} and call `createScoped()` to get a
 * domain-scoped logger. An *unscoped* logger is deliberately reachable for the few event names a
 * downstream system matches by exact equality and which therefore cannot carry a domain prefix.
 */
@Injectable()
export class PcpLoggerService implements PcpLoggerInterface {
    /** Root (unscoped) logger. */
    private readonly root = new ScopedPcpLogger(
        inject(PCP_TELEMETRY_SINK, { optional: true }),
        inject(LOGGING_CONFIG, { optional: true }),
        '',
    );

    verbose(message: string, properties?: Record<string, unknown>): void { this.root.verbose(message, properties); }
    debug(message: string, properties?: Record<string, unknown>): void { this.root.debug(message, properties); }
    info(message: string, properties?: Record<string, unknown>): void { this.root.info(message, properties); }
    warn(message: string, properties?: Record<string, unknown>): void { this.root.warn(message, properties); }
    error(message: string, error?: Error, properties?: Record<string, unknown>): void { this.root.error(message, error, properties); }
    critical(message: string, error?: Error, properties?: Record<string, unknown>): void { this.root.critical(message, error, properties); }
    trackEvent(name: string, properties?: Record<string, unknown>): void { this.root.trackEvent(name, properties); }
    trackMetric(name: string, value: number, properties?: Record<string, unknown>): void { this.root.trackMetric(name, value, properties); }
    createScoped(domain: string): PcpLoggerInterface { return this.root.createScoped(domain); }
}
